// src/components/dwh/SnowflakeSetup.tsx
//
// Presentational halves of the BYO-Snowflake branch of the DWH setup wizard
// (ADR-0175 fase 2). State + API calls live in `DwhSetupModal.tsx`, which owns
// the stepper chrome and the footer buttons for every branch — these two
// components only render the step body.
//
//   <SnowflakeSetupForm />         — step 2: address fields + advanced overrides
//   <SnowflakeConnectInstructions />— step 3: grant script, fingerprint, errors
//
// The `grant_script` asymmetry is load-bearing here: it comes back from the
// connect POST only, never from the status GET. So when the wizard resumes a
// PENDING_VALIDATION account there is nothing to show until the customer asks
// for it, and "Show grant script" re-POSTs connect (idempotent, reuses the
// stored keypair).

import { lazy, Suspense } from "react";
import {
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Code,
  CopyButton,
  Group,
  Loader,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  Textarea,
  UnstyledButton,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconCheck,
  IconChevronDown,
  IconChevronRight,
  IconCopy,
  IconFingerprint,
} from "@tabler/icons-react";

import {
  SNOWFLAKE_DEFAULTS,
  type SnowflakeConnectFormValues,
  type SnowflakeDwhConnectResult,
  type SnowflakeDwhStatus,
} from "@/api/dataWarehouseSnowflake";
import type { SnowflakeFormErrors } from "./snowflakeValidation";

// Same lazy-load pattern as QuestionResultCard/MessageRichContent: highlight.js
// is heavy and this wizard is on the onboarding path. The Suspense fallback
// renders the SAME script text in a plain <Code> block, so the customer (and a
// test) never sees a hole while the chunk loads.
const CodeHighlightLazy = lazy(async () => {
  const mod = await import("@mantine/code-highlight");
  await import("@mantine/code-highlight/styles.css");
  return { default: mod.CodeHighlight };
});

export const EMPTY_SNOWFLAKE_FORM: SnowflakeConnectFormValues = {
  account: "",
  database: "",
  warehouse: "",
  role: "",
  user: "",
  privateKeyPem: "",
};

// ── Step 2: the connect form ────────────────────────────────────

export function SnowflakeSetupForm({
  values,
  onChange,
  errors,
  advancedOpen,
  onToggleAdvanced,
}: {
  values: SnowflakeConnectFormValues;
  onChange: (patch: Partial<SnowflakeConnectFormValues>) => void;
  errors: SnowflakeFormErrors;
  advancedOpen: boolean;
  onToggleAdvanced: () => void;
}) {
  // A "required" error on an untouched field is noise — only surface a field's
  // error once it actually holds something. Same rule as the managed-name
  // field on the BigQuery branch.
  const shown = (field: keyof SnowflakeConnectFormValues) =>
    values[field].trim() ? errors[field] : undefined;

  return (
    <Stack gap="md">
      <TextInput
        label="Account identifier"
        description="Found in Snowflake under Admin → Accounts, or in your account URL."
        placeholder="MYORG-ACCOUNT1"
        value={values.account}
        onChange={(e) => onChange({ account: e.currentTarget.value })}
        error={shown("account")}
        required
      />

      <TextInput
        label="Database"
        description="The database Verity gets access to. Spelling is taken verbatim."
        placeholder="VERITY_DB"
        value={values.database}
        onChange={(e) => onChange({ database: e.currentTarget.value })}
        error={shown("database")}
        required
      />

      <UnstyledButton onClick={onToggleAdvanced} aria-expanded={advancedOpen}>
        <Group gap={4}>
          {advancedOpen ? (
            <IconChevronDown size={14} />
          ) : (
            <IconChevronRight size={14} />
          )}
          <Text size="sm" c="blue">
            Advanced options
          </Text>
        </Group>
      </UnstyledButton>

      {advancedOpen && (
        <Stack gap="md">
          <Text size="xs" c="dimmed">
            Leave these empty to use the names in the grant script Verity
            generates for you.
          </Text>

          <TextInput
            label="Warehouse"
            placeholder={SNOWFLAKE_DEFAULTS.warehouse}
            value={values.warehouse}
            onChange={(e) => onChange({ warehouse: e.currentTarget.value })}
            error={shown("warehouse")}
          />
          <TextInput
            label="Role"
            placeholder={SNOWFLAKE_DEFAULTS.role}
            value={values.role}
            onChange={(e) => onChange({ role: e.currentTarget.value })}
            error={shown("role")}
          />
          <TextInput
            label="Service user"
            placeholder={SNOWFLAKE_DEFAULTS.user}
            value={values.user}
            onChange={(e) => onChange({ user: e.currentTarget.value })}
            error={shown("user")}
          />
          <Textarea
            label="Your own private key (optional)"
            description="Paste an RSA private key in PKCS#8 PEM format to use a key you already manage. Leave empty and Verity generates one for you."
            placeholder="-----BEGIN PRIVATE KEY-----"
            value={values.privateKeyPem}
            onChange={(e) => onChange({ privateKeyPem: e.currentTarget.value })}
            error={shown("privateKeyPem")}
            autosize
            minRows={3}
            maxRows={6}
            spellCheck={false}
            autoComplete="off"
            styles={{ input: { fontFamily: "monospace", fontSize: 12 } }}
          />
        </Stack>
      )}
    </Stack>
  );
}

// ── Step 3: grant script + validation ───────────────────────────

function GrantScriptBlock({ script }: { script: string }) {
  return (
    <Box
      style={{
        border: "1px solid var(--app-border, var(--mantine-color-gray-3))",
        borderRadius: "var(--mantine-radius-sm)",
        overflow: "hidden",
      }}
    >
      <ScrollArea.Autosize mah={220} type="auto">
        <Suspense
          fallback={
            <Code block style={{ fontSize: 12 }}>
              {script}
            </Code>
          }
        >
          <CodeHighlightLazy
            code={script}
            language="sql"
            withCopyButton={false}
          />
        </Suspense>
      </ScrollArea.Autosize>
    </Box>
  );
}

export function SnowflakeConnectInstructions({
  connectResult,
  status,
  productName,
  onShowGrantScript,
  showingGrantScript,
  validationErrors,
  validated,
}: {
  /** Result of the most recent connect POST — the only source of grant_script. */
  connectResult: SnowflakeDwhConnectResult | null;
  /** Stored config from the status GET, used when resuming a session. */
  status: SnowflakeDwhStatus | null;
  productName: string;
  onShowGrantScript: () => void;
  showingGrantScript: boolean;
  validationErrors: string[];
  /** True once a validate attempt has come back with errors (adds retry copy). */
  validated: boolean;
}) {
  const grantScript = connectResult?.grant_script ?? null;
  const fingerprint =
    connectResult?.public_key_fingerprint ?? status?.public_key_fingerprint ?? null;
  const sfAccount =
    connectResult?.snowflake_account ?? status?.snowflake_account ?? null;
  const database =
    connectResult?.snowflake_database ?? status?.snowflake_database ?? null;
  const role = connectResult?.snowflake_role ?? status?.snowflake_role ?? null;

  return (
    <Stack gap="md">
      <Stack gap={4}>
        <Text size="sm" fw={600}>
          1. Send this script to your Snowflake administrator
        </Text>
        <Text size="sm" c="dimmed">
          It creates one service user and one role with access to{" "}
          {database ? (
            <Text span ff="monospace" fw={500}>
              {database}
            </Text>
          ) : (
            "your database"
          )}{" "}
          only — no account-level grants. It has to be run once as{" "}
          <Text span fw={500}>
            ACCOUNTADMIN
          </Text>
          .
        </Text>

        {grantScript ? (
          <Stack gap={4} mt={4}>
            <Group justify="space-between">
              <Group gap="xs">
                {sfAccount && (
                  <Badge variant="light" size="xs">
                    {sfAccount}
                  </Badge>
                )}
                {role && (
                  <Badge variant="light" size="xs" color="gray">
                    {role}
                  </Badge>
                )}
              </Group>
              <CopyButton value={grantScript}>
                {({ copied, copy }) => (
                  <Button
                    size="compact-sm"
                    variant="light"
                    color={copied ? "green" : "blue"}
                    leftSection={
                      copied ? <IconCheck size={14} /> : <IconCopy size={14} />
                    }
                    onClick={copy}
                  >
                    {copied ? "Copied" : "Copy script"}
                  </Button>
                )}
              </CopyButton>
            </Group>
            <GrantScriptBlock script={grantScript} />
          </Stack>
        ) : (
          <Group gap="xs" mt={4}>
            {showingGrantScript ? (
              <Group gap="xs">
                <Loader size="xs" />
                <Text size="sm" c="dimmed">
                  Fetching your grant script...
                </Text>
              </Group>
            ) : (
              <Anchor component="button" type="button" size="sm" onClick={onShowGrantScript}>
                Show grant script again
              </Anchor>
            )}
          </Group>
        )}
      </Stack>

      {fingerprint && (
        <Stack gap={4}>
          <Text size="sm" fw={600}>
            2. Check the key fingerprint
          </Text>
          <Text size="sm" c="dimmed">
            After running the script, your administrator can confirm the key
            with <Text span ff="monospace">DESC USER</Text> — it must match:
          </Text>
          <Group gap={6} wrap="nowrap">
            <IconFingerprint size={14} />
            <Text
              size="xs"
              ff="monospace"
              style={{ overflowWrap: "anywhere" }}
              data-testid="snowflake-key-fingerprint"
            >
              {fingerprint}
            </Text>
          </Group>
        </Stack>
      )}

      <Stack gap={4}>
        <Text size="sm" fw={600}>
          {fingerprint ? "3." : "2."} Validate access
        </Text>
        <Text size="sm" c="dimmed">
          Once the script has been run, click{" "}
          <Text span fw={500}>
            Validate access
          </Text>{" "}
          below and {productName} will connect to check the grants.
        </Text>
      </Stack>

      {validationErrors.length > 0 && (
        <Stack gap="xs">
          {validationErrors.map((err, i) => (
            <Alert key={i} color="red" icon={<IconAlertCircle size={16} />}>
              {err}
            </Alert>
          ))}
          {validated && (
            <Text size="xs" c="dimmed">
              Fix the grants above and click Validate access again — nothing is
              lost in the meantime.
            </Text>
          )}
        </Stack>
      )}
    </Stack>
  );
}
